// src/context/UserContext.jsx
import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import api from "../api/axios";

import {
  currentUserApi,
  signInApi,
  signOutApi,
  userRequestOtpApi,
  userVerifyOtpApi,
  userCompleteProfileApi,
} from "../api/authApi";

import {
  ownerSignInApi,
  currentOwnerApi,
  ownerSignOutApi,
  ownerVerifyOtpApi,
  ownerPasswordSignInApi,
  fetchOwnerProfile,
} from "../api/ownerPortalApi";

const UserContext = createContext(null);

// Match backend enum values
export const ROLES = {
  USER: "USER",
  OWNER: "OWNER",
  DRIVER: "DRIVER",
};

function cleanPhone(p = "") {
  return String(p).replace(/\D/g, "").slice(-10);
}

function applyToken(token) {
  if (token) {
    api.defaults.headers.common.Authorization = `Bearer ${token}`;
    localStorage.setItem("token", token);
  } else {
    delete api.defaults.headers.common.Authorization;
    localStorage.removeItem("token");
  }
}

function normalizeUser(me) {
  if (!me) return null;
  const id =
    typeof me.id === "number" ? me.id : typeof me.userId === "number" ? me.userId : null;

  const phoneNumber = me.phoneNumber ?? me.phone ?? null;
  const email = me.email ?? null;
  const userName = me.userName ?? me.name ?? null;
  const role = me.role ?? ROLES.USER;

  if (!id && !phoneNumber && !email) return null;

  return { id, phoneNumber, email, userName, role };
}

async function fetchOwnerProfileSafe() {
  try {
    const owner = await fetchOwnerProfile();
    if (owner) return owner;
  } catch {}
  try {
    const owner = await currentOwnerApi();
    if (owner) return owner;
  } catch {}
  return null;
}

export function UserProvider({ children }) {
  // shape: { id?, email?, phoneNumber?, role, userName?, ... }
  const [user, setUserState] = useState(null);
  const [booted, setBooted] = useState(false);

  // Boot from storage → token → /current-user (USER) OR /owners/profile (OWNER)
  useEffect(() => {
    (async () => {
      try {
        const rawUser = localStorage.getItem("user");
        let parsed = null;
        if (rawUser) {
          try { parsed = JSON.parse(rawUser); } catch {}
          if (parsed && (parsed.id || parsed.email || parsed.phoneNumber)) {
            setUserState(parsed);
          }
        }

        const token = localStorage.getItem("token");
        if (!token) return;

        applyToken(token);

        // OWNER session
        if (parsed?.role === ROLES.OWNER) {
          try {
            const owner = await fetchOwnerProfileSafe();
            if (owner) {
              const next = {
                ...(owner || {}),
                email: owner.email || parsed.email || "",
                phoneNumber: owner.phoneNumber || parsed.phoneNumber || null,
                role: ROLES.OWNER,
                userName: owner.name || owner.userName || parsed?.userName || null,
              };
              setUserState(next);
              localStorage.setItem("user", JSON.stringify(next));
            }
          } finally {
            setBooted(true);
          }
          return;
        }

        // USER session
        try {
          const me = await currentUserApi();
          const next = normalizeUser(me);
          if (next) {
            setUserState(next);
            localStorage.setItem("user", JSON.stringify(next));
          }
        } catch {
          // keep parsed if any
        }
      } finally {
        setBooted(true);
      }
    })();
  }, []);

  /** ---------------- USER: legacy email/password ---------------- */
  const signIn = async ({ email, password }) => {
    const res = await signInApi({ email, password });
    if (!res?.token) throw new Error("Invalid sign-in response");
    applyToken(res.token);

    try {
      const me = await currentUserApi();
      const next = normalizeUser(me) || { email: res.email, role: ROLES.USER };
      localStorage.setItem("user", JSON.stringify(next));
      setUserState(next);
      return next;
    } catch {
      const minimal = { email: res.email, role: ROLES.USER };
      localStorage.setItem("user", JSON.stringify(minimal));
      setUserState(minimal);
      return minimal;
    }
  };

  /** ---------------- USER: OTP request ---------------- */
  const userRequestOtp = async (phoneNumber) => {
    const phone = cleanPhone(phoneNumber);
    if (phone.length !== 10) throw new Error("Enter a valid 10-digit phone number.");
    await userRequestOtpApi({ phoneNumber: phone });
  };

  /** ---------------- USER: OTP verify/sign-in ---------------- */
  const userSignInOtp = async ({ phoneNumber, otp }) => {
    const phone = cleanPhone(phoneNumber);
    const res = await userVerifyOtpApi({ phoneNumber: phone, otp: String(otp || "").trim() });

    if (!res?.token) throw new Error(res?.message || "OTP verification failed");

    // store token + minimal user immediately
    applyToken(res.token);
    const minimal = { phoneNumber: phone, role: ROLES.USER };
    localStorage.setItem("user", JSON.stringify(minimal));
    setUserState(minimal);

    if (res?.isNewUser) {
      return { ...minimal, isNewUser: true };
    }

    // refresh canonical user
    try {
      const me = await currentUserApi();
      const next = normalizeUser(me) || minimal;
      localStorage.setItem("user", JSON.stringify(next));
      setUserState(next);
      return next;
    } catch {
      return minimal;
    }
  };

  /** ---------------- USER: profile completion ---------------- */
  const userCompleteProfile = async (payload) => {
    await userCompleteProfileApi(payload);
    const me = await currentUserApi();
    const next = normalizeUser(me);
    if (!next) throw new Error("Could not load user after profile completion.");
    localStorage.setItem("user", JSON.stringify(next));
    setUserState(next);
    return next;
  };

  /** ---------------- OWNER sign-in (your existing code, kept) ---------------- */
  const ownerSignIn = async (payload) => {
    // OTP
    if (payload && payload.mode === "otp") {
      const phoneNumber = cleanPhone(payload.phoneNumber);
      const hasName = !!payload.name?.trim();
      const hasAddr = !!payload.address?.trim();
      const signupIntent = hasName && hasAddr;

      const apiPayload = { phoneNumber, otp: String(payload.otp || "").trim() };
      if (signupIntent) {
        apiPayload.name = payload.name.trim();
        apiPayload.address = payload.address.trim();
        if (payload.email?.trim()) apiPayload.email = payload.email.trim();
      }

      const res = await ownerVerifyOtpApi(apiPayload);

      if (res?.isNewOwner && !signupIntent) {
        if (res?.token) applyToken(res.token);
        return { email: "", role: ROLES.OWNER, userName: null, phoneNumber, isNewOwner: true };
      }

      if (!res?.token) throw new Error("Invalid owner OTP verify response");

      applyToken(res.token);
      const ownerProfile = await fetchOwnerProfileSafe();

      const next = {
        ...(ownerProfile || {}),
        email: ownerProfile?.email || payload.email || "",
        phoneNumber: ownerProfile?.phoneNumber || phoneNumber,
        role: ROLES.OWNER,
        userName: ownerProfile?.name || ownerProfile?.userName || payload.name || null,
      };

      localStorage.setItem("user", JSON.stringify(next));
      if (next.email) localStorage.setItem("ownerEmail", next.email);
      setUserState(next);
      return { ...next, isNewOwner: !!res?.isNewOwner };
    }

    // Password
    if (payload && payload.mode === "password") {
      const phoneNumber = cleanPhone(payload.phoneNumber);
      const res = await ownerPasswordSignInApi({ phoneNumber, password: payload.password });
      if (!res?.token) throw new Error("Invalid owner password response");

      applyToken(res.token);
      const ownerProfile = await fetchOwnerProfileSafe();

      const next = {
        ...(ownerProfile || {}),
        email: ownerProfile?.email || "",
        phoneNumber: ownerProfile?.phoneNumber || phoneNumber,
        role: ROLES.OWNER,
        userName: ownerProfile?.name || ownerProfile?.userName || null,
      };

      localStorage.setItem("user", JSON.stringify(next));
      if (next.email) localStorage.setItem("ownerEmail", next.email);
      setUserState(next);
      return next;
    }

    // legacy owner email/password
    const res = await ownerSignInApi({ email: payload.email, password: payload.password });
    if (!res?.token) throw new Error("Invalid owner sign-in response");

    applyToken(res.token);
    const ownerProfile = await fetchOwnerProfileSafe();

    const next = {
      ...(ownerProfile || {}),
      email: ownerProfile?.email || res.email || payload.email || "",
      phoneNumber: ownerProfile?.phoneNumber || null,
      role: ROLES.OWNER,
      userName: ownerProfile?.name || ownerProfile?.userName || res.userName || null,
    };

    localStorage.setItem("user", JSON.stringify(next));
    if (next.email) localStorage.setItem("ownerEmail", next.email);
    setUserState(next);
    return next;
  };

  const signOut = async () => {
    try { await signOutApi(); } catch {}
    try { await ownerSignOutApi(); } catch {}
    applyToken(null);
    localStorage.removeItem("user");
    localStorage.removeItem("ownerEmail");
    localStorage.removeItem("driverPhone");
    setUserState(null);
  };

  // ✅ auth should not require email anymore
  const isAuthenticated = !!(localStorage.getItem("token") || user?.id || user?.phoneNumber || user?.email);

  const role = user?.role || ROLES.USER;

  const value = useMemo(
    () => ({
      user,
      booted,
      role,
      isAuthenticated,
      isUser: role === ROLES.USER,
      isOwner: role === ROLES.OWNER,
      isDriver: role === ROLES.DRIVER,

      signIn,
      userRequestOtp,
      userSignInOtp,
      userCompleteProfile,

      ownerSignIn,
      signOut,

      setUser: (u, token) => {
        if (token !== undefined) applyToken(token);
        if (u) localStorage.setItem("user", JSON.stringify(u));
        else localStorage.removeItem("user");
        setUserState(u || null);
      },

      updateUser: (patch) => {
        setUserState((prev) => {
          const next = { ...(prev || {}), ...(patch || {}) };
          localStorage.setItem("user", JSON.stringify(next));
          return next;
        });
      },
    }),
    [user, booted, role, isAuthenticated]
  );

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
}

export function useUser() {
  const ctx = useContext(UserContext);
  if (!ctx) throw new Error("useUser must be used within <UserProvider />");
  return ctx;
}

/**
 * Route guard
 *   <RequireAuth roles={[ROLES.OWNER]} redirectTo="/login">...</RequireAuth>
 */
export function RequireAuth({ children, roles, redirectTo }) {
  const { user, booted, isAuthenticated } = useUser();
  const location = useLocation();

  if (!booted) {
    return <div className="min-h-[40vh] grid place-items-center text-slate-600">Loading…</div>;
  }

  const myRole = user?.role || ROLES.USER;
  const roleAllowed = !roles || roles.length === 0 || roles.includes(myRole);

  if (!isAuthenticated || !roleAllowed) {
    const next = location.pathname + location.search;
    const target = redirectTo || "/login";
    return (
      <Navigate
        to={`${target}?redirect=${encodeURIComponent(next)}`}
        replace
        state={{ from: next }}
      />
    );
  }

  return children;
}
